import { useState } from "react";
import api, { apiError, fmtMoney, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FileSpreadsheet, FileDown, Calculator, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { exportWorkbook, exportPdfSections } from "@/lib/exporters";

const firstOfMonth = () => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10); };
const today = () => new Date().toISOString().slice(0, 10);
const num = (n) => Math.round(Number(n) || 0);
const day = (s) => (s || "").slice(0, 10);

export default function AccountingExport({ slug }) {
  const [start, setStart] = useState(firstOfMonth());
  const [end, setEnd] = useState(today());
  const [loading, setLoading] = useState(false);

  const build = async () => {
    const [sales, orders, expenses, closures] = await Promise.all([
      api.get(`/store/${slug}/sales`).then((r) => r.data).catch(() => []),
      api.get(`/store/${slug}/orders`).then((r) => r.data).catch(() => []),
      api.get(`/store/${slug}/expenses`).then((r) => r.data).catch(() => []),
      api.get(`/store/${slug}/treasury/closures`).then((r) => r.data).catch(() => []),
    ]);
    const inRange = (d) => { const x = day(d); return x && x >= start && x <= end; };
    const vSales = sales.filter((s) => s.status !== "Annulée" && inRange(s.created_at))
      .map((s) => ({ date: s.created_at, num: s.number, canal: "Comptoir", client: s.customer_name || "Client comptoir", total: s.total, pay: s.payment_method, statut: s.status }));
    const vOrders = orders.filter((o) => o.status !== "Annulée" && inRange(o.created_at))
      .map((o) => ({ date: o.created_at, num: o.number, canal: "En ligne", client: o.customer_name || "—", total: o.total, pay: o.payment_method, statut: o.status }));
    const ventes = [...vSales, ...vOrders].sort((a, b) => (a.date || "").localeCompare(b.date || ""));
    const charges = expenses.filter((e) => inRange(e.date || e.created_at))
      .sort((a, b) => (a.date || a.created_at || "").localeCompare(b.date || b.created_at || ""));
    const clot = closures.filter((c) => inRange(c.created_at))
      .sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""));

    const totalVentes = ventes.reduce((s, v) => s + Number(v.total || 0), 0);
    const totalCharges = charges.reduce((s, e) => s + Number(e.amount || 0), 0);
    const totalEcart = clot.reduce((s, c) => s + Number(c.ecart || 0), 0);
    const resultat = totalVentes - totalCharges;
    return { ventes, charges, clot, totalVentes, totalCharges, totalEcart, resultat };
  };

  const run = async (kind) => {
    setLoading(true);
    try {
      const d = await build();
      if (!d.ventes.length && !d.charges.length && !d.clot.length) {
        toast.info("Aucune donnée sur la période sélectionnée");
        setLoading(false);
        return;
      }
      const periode = `${fmtDate(start)} au ${fmtDate(end)}`;
      const fname = `comptabilite_${start}_${end}`;
      const resume = [
        ["Période", `${start} → ${end}`],
        ["Nombre de ventes", d.ventes.length],
        ["Total des ventes", kind === "xlsx" ? num(d.totalVentes) : fmtMoney(d.totalVentes)],
        ["Total des charges", kind === "xlsx" ? num(d.totalCharges) : fmtMoney(d.totalCharges)],
        ["Résultat (ventes - charges)", kind === "xlsx" ? num(d.resultat) : fmtMoney(d.resultat)],
        ["Écart de caisse cumulé", kind === "xlsx" ? num(d.totalEcart) : (d.totalEcart > 0 ? "+" : "") + fmtMoney(d.totalEcart)],
      ];
      const ventesHead = ["Date", "N°", "Canal", "Client", "Total", "Paiement", "Statut"];
      const chargesHead = ["Date", "Libellé", "Catégorie", "Montant", "Fournisseur", "Statut"];
      const clotHead = ["Date", "Caisse", "Ouverture", "Encaissé", "Décaissé", "Attendu", "Compté", "Écart", "Opérateur"];

      if (kind === "xlsx") {
        exportWorkbook(`${fname}.xlsx`, [
          { name: "Résumé", headers: ["Indicateur", "Valeur"], rows: resume },
          { name: "Ventes", headers: ventesHead, rows: d.ventes.map((v) => [day(v.date), v.num, v.canal, v.client, num(v.total), v.pay, v.statut]) },
          { name: "Charges", headers: chargesHead, rows: d.charges.map((e) => [day(e.date || e.created_at), e.label, e.category, num(e.amount), e.supplier, e.status]) },
          { name: "Clotures Z", headers: clotHead, rows: d.clot.map((c) => [day(c.created_at), c.account_name, c.opening != null ? num(c.opening) : "", num(c.total_in), num(c.total_out), num(c.expected), num(c.counted), num(c.ecart), c.user]) },
        ]);
      } else {
        exportPdfSections(`${fname}.pdf`, "Rapport comptable — Vision POS", periode, [
          { heading: "Résumé de la période", headers: ["Indicateur", "Valeur"], rows: resume },
          { heading: `Ventes (${d.ventes.length})`, headers: ventesHead, rows: d.ventes.map((v) => [day(v.date), v.num, v.canal, v.client, fmtMoney(v.total), v.pay, v.statut]) },
          { heading: `Charges (${d.charges.length})`, headers: chargesHead, rows: d.charges.map((e) => [day(e.date || e.created_at), e.label, e.category, fmtMoney(e.amount), e.supplier, e.status]) },
          { heading: `Clôtures Z (${d.clot.length})`, headers: clotHead, rows: d.clot.map((c) => [day(c.created_at), c.account_name, c.opening != null ? fmtMoney(c.opening) : "—", fmtMoney(c.total_in), fmtMoney(c.total_out), fmtMoney(c.expected), fmtMoney(c.counted), (c.ecart > 0 ? "+" : "") + fmtMoney(c.ecart), c.user]) },
        ]);
      }
      toast.success("Export comptable généré");
    } catch (e) { toast.error(apiError(e)); }
    setLoading(false);
  };

  return (
    <div className="vp-card rounded-xl p-6 mb-8" data-testid="accounting-export">
      <h2 className="font-display text-2xl font-bold text-white mb-1 flex items-center gap-2"><Calculator size={20} /> Export comptable consolidé</h2>
      <p className="text-sm text-slate-400 mb-4">Générez un rapport unique (ventes + charges + clôtures Z + résumé) sur la période de votre choix, prêt pour la comptabilité.</p>
      <div className="flex flex-wrap items-end gap-4">
        <div><Label className="text-slate-300 text-sm mb-1.5 block">Du</Label>
          <Input type="date" value={start} max={end} onChange={(e) => setStart(e.target.value)} className="bg-black/30 border-white/10 text-white" data-testid="acct-start" /></div>
        <div><Label className="text-slate-300 text-sm mb-1.5 block">Au</Label>
          <Input type="date" value={end} min={start} onChange={(e) => setEnd(e.target.value)} className="bg-black/30 border-white/10 text-white" data-testid="acct-end" /></div>
        <Button onClick={() => run("xlsx")} disabled={loading} className="vp-accent-bg text-black font-medium" data-testid="acct-export-xlsx">{loading ? <Loader2 className="animate-spin mr-2" size={15} /> : <FileSpreadsheet size={15} className="mr-1" />} Excel</Button>
        <Button onClick={() => run("pdf")} disabled={loading} variant="outline" className="border-white/15 text-slate-200 bg-transparent hover:bg-white/5" data-testid="acct-export-pdf">{loading ? <Loader2 className="animate-spin mr-2" size={15} /> : <FileDown size={15} className="mr-1" />} PDF</Button>
      </div>
    </div>
  );
}
